import { z } from "zod";
import { findApiEndpoint, type ApiEndpointMeta, type HttpMethod } from "./api-endpoints";
import { APP_ROUTES, NAV_ROUTE_KEYS, PROTECTED_ROUTE_KEYS } from "./app-metadata";
import { TRANSACTION_TYPE_METADATA, TRANSACTION_TYPE_VALUES } from "../inventory/transaction-types";

export const USER_ROLES = ["admin", "manager", "chemist"] as const;
export const userRoleSchema = z.enum(USER_ROLES);

export type UserRole = z.infer<typeof userRoleSchema>;

function navPathsForRole(role: UserRole): string[] {
  return NAV_ROUTE_KEYS
    .filter((key) => APP_ROUTES[key].visibleTo.includes(role))
    .map((key) => APP_ROUTES[key].path);
}

function protectedAppPathsForRole(role: UserRole): string[] {
  return PROTECTED_ROUTE_KEYS
    .filter((key) => APP_ROUTES[key].visibleTo.includes(role))
    .map((key) => APP_ROUTES[key].path);
}

export function allowedRolesFor(entry: ApiEndpointMeta, method: HttpMethod): readonly UserRole[] {
  if (entry.access !== "tenant") return USER_ROLES;
  const page = entry.methodOwnerPages?.[method] ?? entry.ownerPage;
  if (page) return APP_ROUTES[page].visibleTo;
  return entry.roles === "all" ? USER_ROLES : entry.roles ?? [];
}

export function isValidUserRole(role: string | null | undefined): role is UserRole {
  return userRoleSchema.safeParse(role).success;
}

export function isChemistRole(role: string | null | undefined): boolean {
  return role === "chemist";
}

export function isAdminRole(role: string | null | undefined): role is Extract<UserRole, "admin"> {
  return role === "admin";
}

export function hasManagerAccess(role: string | null | undefined): role is Extract<UserRole, "admin" | "manager"> {
  return role === "admin" || role === "manager";
}

export function canManageUsers(role: string | null | undefined): boolean {
  return isAdminRole(role);
}

export function canManageDocuments(role: string | null | undefined): boolean {
  return isValidUserRole(role);
}

export const FINANCIAL_ROLES = ["admin", "manager"] as const satisfies readonly UserRole[];

export function canViewFinancials(role: string | null | undefined): boolean {
  return isValidUserRole(role) && (FINANCIAL_ROLES as readonly UserRole[]).includes(role);
}

export const COMMERCIAL_HISTORY_FIELDS = [
  "price",
  "supplierId",
  "supplierContactId",
  "purchaserId",
  "purchaserContactId",
  "referenceNumber",
] as const;

export function filterCommercialHistoryForRole<T extends { fieldName: string | null }>(
  entries: T[],
  role: string | null | undefined,
): T[] {
  if (canViewFinancials(role)) return entries;
  const commercial = new Set<string>(COMMERCIAL_HISTORY_FIELDS);
  return entries.filter((entry) => !(entry.fieldName != null && commercial.has(entry.fieldName)));
}

export function getAllowedTransactionTypes(role: string | null | undefined): readonly string[] {
  if (!isValidUserRole(role)) return [];
  return TRANSACTION_TYPE_VALUES.filter((transactionType) => {
    const allowedRoles: readonly string[] = TRANSACTION_TYPE_METADATA[transactionType].allowedRoles;
    return allowedRoles.includes(role);
  });
}

export function canCreateTransactionType(
  role: string | null | undefined,
  transactionType: string,
): boolean {
  return getAllowedTransactionTypes(role).includes(transactionType);
}

export function canDeleteTransaction(role: string | null | undefined): boolean {
  return isAdminRole(role);
}

export function canViewFullUserDetails(
  role: string | null | undefined,
  currentUserId: number | null | undefined,
  targetUserId: number,
): boolean {
  return isAdminRole(role) || currentUserId === targetUserId;
}

export function canViewBasicUserDetails(
  role: string | null | undefined,
  currentUserId: number | null | undefined,
  targetUserId: number,
): boolean {
  return canViewFullUserDetails(role, currentUserId, targetUserId) || role === "manager";
}

export function canListAllUsers(role: string | null | undefined): boolean {
  return isValidUserRole(role);
}

function matchesAccessScope(paths: readonly string[], candidate: string): boolean {
  return paths.some((entry) => candidate === entry || candidate.startsWith(`${entry}/`));
}

export function canAccessProtectedAppPath(role: string | null | undefined, path: string): boolean {
  if (!isValidUserRole(role)) return false;
  return matchesAccessScope(protectedAppPathsForRole(role), path);
}

export function getVisibleNavigationPaths(role: string | null | undefined): string[] {
  if (!isValidUserRole(role)) return [];
  return navPathsForRole(role);
}

export function canAccessApiPath(role: string | null | undefined, path: string, method: HttpMethod = "GET"): boolean {
  if (!isValidUserRole(role)) return false;
  const entry = findApiEndpoint(path);
  return entry != null && allowedRolesFor(entry, method).includes(role);
}
